import React, { useState, useEffect } from "react";
import { useCart } from "../../Modules/Hooks/useCart";
import "./Cart.css";
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  Truck,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../config";
import Image from "../../Components/Image/Image";
import { deliveryService } from "../../Modules/Services/delivery.service";
import { ordersService } from "../../Modules/Services/orders.service";
import settingsService from "../../Modules/Services/settings.service";

const Cart = () => {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    totalItems,
    totalPrice,
    clearCart,
  } = useCart();
  const [deliveryType, setDeliveryType] = useState("pickup");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [timeMode, setTimeMode] = useState("asap"); // asap | scheduled
  const [orderTime, setOrderTime] = useState("");
  const [pickupPlace, setPickupPlace] = useState("");
  const [savedPhone, setSavedPhone] = useState("");
  const [savedAddress, setSavedAddress] = useState("");
  const [contacts, setContacts] = useState({
    phoneNumber: null,
    telegram: null,
  });
  const [orderStatus, setOrderStatus] = useState({
    loading: true,
    accepting: true,
    reason: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [freeDeliveryPriceThreshold, setFreeDeliveryPriceThreshold] =
    useState(2500);
  const [isLoadingThreshold, setIsLoadingThreshold] = useState(false);
  const minimumDeliveryPriceThreshold = 2500; // Самая минимальная возможная сумма доставки. Используется в том случае, если не удалось правильно рассчитать расстояние до клиента.

  useEffect(() => {
    try {
      const fromStorage = localStorage.getItem("shahrizada-phone");
      if (fromStorage) setSavedPhone(fromStorage);

      const savedAddr = localStorage.getItem("shahrizada-address");
      if (savedAddr) setSavedAddress(savedAddr);
    } catch (e) {
      // ignore
    }

    const fetchContacts = async () => {
      try {
        const data = await settingsService.getContacts();
        setContacts({
          phoneNumber: data?.phoneNumber || null,
          telegram: data?.telegram || null,
        });
      } catch (e) {
        // ignore
      }
    };

    const fetchOrderStatus = async () => {
      try {
        setOrderStatus((s) => ({ ...s, loading: true }));
        const data = await settingsService.getOrderStatus();
        setOrderStatus({
          loading: false,
          accepting: Boolean(data?.accepting),
          reason: data?.reason || null,
        });
      } catch (e) {
        // Если не удалось загрузить статус — не блокируем заказы
        setOrderStatus({ loading: false, accepting: true, reason: null });
      }
    };

    fetchContacts();
    fetchOrderStatus();
  }, []);

  // Обработчик изменения адреса с debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (deliveryType === "delivery" && address.trim()) {
        fetchDeliveryThreshold(address);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [address, deliveryType]);

  const buildOrderPayload = () => {
    const effectiveTime = timeMode === "asap" ? "по готовности" : orderTime;
    const items = cart.map((item) => ({
      product_id: item.product_id,
      variant_id: item.variant_id,
      product_name: item.product_name,
      size: item.size || null,
      weight: item.weight || null,
      price: item.price,
      quantity: item.quantity,
    }));

    if (deliveryType === "pickup") {
      return {
        type: "pickup",
        phone,
        time: effectiveTime,
        pickup_place: pickupPlace,
        items,
      };
    }

    return {
      type: "delivery",
      phone,
      time: effectiveTime,
      delivery: {
        address,
        meta: {
          freeDeliveryThreshold: freeDeliveryPriceThreshold,
          amountToFreeDelivery,
          isFreeDelivery: amountToFreeDelivery === 0,
        },
      },
      items,
    };
  };

  const persistPhone = (value) => {
    try {
      localStorage.setItem("shahrizada-phone", value);
      setSavedPhone(value);
    } catch (e) {
      // ignore
    }
  };

  const persistAddress = (value) => {
    try {
      localStorage.setItem("shahrizada-address", value);
      setSavedAddress(value);
    } catch (e) {
      // ignore
    }
  };

  const handleCreateOrder = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setCreatedOrderId(null);

    if (!orderStatus.loading && !orderStatus.accepting) {
      setSubmitError(
        orderStatus.reason
          ? `Сейчас нельзя оформить заказ. Причина: ${orderStatus.reason}`
          : "Сейчас нельзя оформить заказ."
      );
      return;
    }

    const isTimeValid = timeMode === "asap" || Boolean(orderTime);
    const isPickupValid = Boolean(phone && isTimeValid);
    const isDeliveryValid = Boolean(phone && address && isTimeValid);
    const isValid =
      deliveryType === "delivery" ? isDeliveryValid : isPickupValid;

    if (!isValid) {
      setSubmitError("Заполните обязательные поля");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildOrderPayload();
      const result = await ordersService.createOrder(payload);
      if (!result?.success) {
        throw new Error(result?.error || "Не удалось создать заказ");
      }

      persistPhone(phone);
      if (deliveryType === "delivery" && address.trim()) {
        persistAddress(address.trim());
      }
      setCreatedOrderId(result.data?.orderId ?? null);
      setSubmitSuccess("Заказ создан!");
      clearCart();
    } catch (error) {
      const serverMsg =
        error?.response?.data?.error || error?.response?.data?.message || null;
      const serverReason = error?.response?.data?.reason || null;
      if (error?.response?.status === 403 && (serverMsg || serverReason)) {
        setSubmitError(
          serverReason
            ? `Сейчас нельзя оформить заказ. Причина: ${serverReason}`
            : serverMsg
        );
      } else {
        setSubmitError(error?.message || "Ошибка при создании заказа");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess || createdOrderId) {
    return (
      <div className="Page--Container Cart--Empty">
        <div className="Cart--SubmitNotice Cart--SubmitNotice--success">
          <div className="Cart--SuccessTitle">Спасибо! Заказ принят.</div>
          <div className="Cart--SuccessSubtitle">
            Вам позвонят для уточнения заказа. Если нужно — напишите нам.
          </div>
          {createdOrderId && (
            <div className="Cart--OrderIdRow">
              Номер заказа: <b>#{createdOrderId}</b>
            </div>
          )}
          {(contacts?.phoneNumber || contacts?.telegram) && (
            <div className="Cart--Contacts">
              <div className="Cart--ContactsTitle">Контакты</div>
              {contacts?.phoneNumber && (
                <div>
                  Телефон: <b>{contacts.phoneNumber}</b>
                </div>
              )}
              {contacts?.telegram && (
                <div>
                  Telegram:{" "}
                  <a href={contacts.telegram} target="_blank" rel="noreferrer">
                    <u>{contacts.telegram}</u>
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
        <Link to="/" className="accent--button">
          Перейти в меню
        </Link>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="Page--Container Cart--Empty">
        <h2>Ваша корзина пуста</h2>
        <p>Добавьте что-нибудь из меню, чтобы сделать заказ.</p>
        <Link to="/" className="accent--button">
          Перейти в меню
        </Link>
      </div>
    );
  }

  const isTimeValid = timeMode === "asap" || Boolean(orderTime);
  const isPickupFormValid = Boolean(phone && isTimeValid);
  const isDeliveryFormValid = Boolean(phone && address && isTimeValid);
  const isFormValid =
    deliveryType === "delivery" ? isDeliveryFormValid : isPickupFormValid;
  const isOrderingDisabled = !orderStatus.loading && !orderStatus.accepting;

  const amountToFreeDelivery = Math.max(
    0,
    freeDeliveryPriceThreshold - totalPrice
  );

  // Функция для получения порога бесплатной доставки с сервера
  const fetchDeliveryThreshold = async (address) => {
    if (!address.trim()) {
      setFreeDeliveryPriceThreshold(minimumDeliveryPriceThreshold);
      return;
    }

    setIsLoadingThreshold(true);
    try {
      const response = await deliveryService.getDeliveryThreshold(address);
      const threshold =
        response.data?.minOrderAmount || minimumDeliveryPriceThreshold;
      setFreeDeliveryPriceThreshold(threshold);
    } catch (error) {
      console.error("Ошибка при получении порога доставки:", error);
      setFreeDeliveryPriceThreshold(minimumDeliveryPriceThreshold);
    } finally {
      setIsLoadingThreshold(false);
    }
  };

  return (
    <div className="Page--Container Cart--Container">
      <div className="Cart--ItemsList">
        <h1>Ваша корзина</h1>
        {cart.map((item) => (
          <div key={item.variant_id} className="Cart--Item">
            <Image
              src={`${API_BASE_URL}${item.image_url}`}
              alt={item.product_name}
              className="Cart--ItemImage"
            />
            <div className="Cart--ItemInfo">
              <div>
                <div className="Cart--ItemInfo__top">
                  <h4>{item.product_name}</h4>
                  <div className="Cart--ItemPrice">
                    {(item.price * item.quantity).toFixed(2)} ₽
                  </div>
                </div>

                <p className="Cart--ItemOption">{item.size || item.weight}</p>
              </div>
              <div className="Cart--ItemInfo__bottom">
                <div className="Cart--ItemQuantity">
                  <button
                    onClick={() =>
                      updateQuantity(item.variant_id, item.quantity - 1)
                    }
                  >
                    <Minus size={16} />
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    onClick={() =>
                      updateQuantity(item.variant_id, item.quantity + 1)
                    }
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
            <div className="Cart--ItemRemove">
              <button onClick={() => removeFromCart(item.variant_id)}>
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="Cart--Summary">
        <h3>Итог заказа</h3>
        <div className="Summary--Row">
          <span>Товары ({totalItems} шт.)</span>
          <span>{totalPrice.toFixed(2)} ₽</span>
        </div>
        <div className="Summary--Row Total--Row">
          <span>Итого</span>
          <span>{totalPrice.toFixed(2)} ₽</span>
        </div>

        <div className="Cart--DeliveryOptions">
          <button
            className={`Delivery--OptionButton ${
              deliveryType === "pickup" ? "active" : ""
            }`}
            onClick={() => setDeliveryType("pickup")}
          >
            <ShoppingBag size={24} />
            <span>Самовывоз</span>
          </button>
          <button
            className={`Delivery--OptionButton ${
              deliveryType === "delivery" ? "active" : ""
            }`}
            onClick={() => setDeliveryType("delivery")}
          >
            <Truck size={24} />
            <span>Доставка</span>
          </button>
        </div>

        {submitError && (
          <div className="Cart--SubmitNotice Cart--SubmitNotice--error">
            {submitError}
          </div>
        )}
        {submitSuccess && (
          <div className="Cart--SubmitNotice Cart--SubmitNotice--success">
            {submitSuccess}
          </div>
        )}

        {isOrderingDisabled && (
          <div className="Cart--OrderClosed">
            <div className="Cart--OrderClosed__header">
              <AlertCircle size={18} />
              <div className="Cart--OrderClosed__title">
                Приём заказов временно приостановлен
              </div>
            </div>
            <div className="Cart--OrderClosed__text">
              {orderStatus.reason
                ? `Причина: ${orderStatus.reason}`
                : "Попробуйте оформить заказ позже."}
            </div>
          </div>
        )}

        {savedPhone && !phone && (
          <div className="Cart--SavedPhone">
            <div className="Cart--SavedPhoneText">
              Сохранённый номер: <b>{savedPhone}</b>
            </div>
            <button
              className="Cart--SavedPhoneButton"
              type="button"
              onClick={() => setPhone(savedPhone)}
            >
              Вставить
            </button>
          </div>
        )}

        {deliveryType === "delivery" && savedAddress && !address && (
          <div className="Cart--SavedPhone">
            <div className="Cart--SavedPhoneText">
              Сохранённый адрес: <b>{savedAddress}</b>
            </div>
            <button
              className="Cart--SavedPhoneButton"
              type="button"
              onClick={() => setAddress(savedAddress)}
            >
              Вставить
            </button>
          </div>
        )}

        {deliveryType === "pickup" && (
          <div className="Cart--DeliveryForm">
            <input
              type="tel"
              placeholder="Номер телефона"
              className="Cart--Input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div className="Cart--TimeOptions">
              <button
                type="button"
                className={`Time--OptionButton ${
                  timeMode === "asap" ? "active" : ""
                }`}
                onClick={() => setTimeMode("asap")}
              >
                Забрать по готовности
              </button>
              <button
                type="button"
                className={`Time--OptionButton ${
                  timeMode === "scheduled" ? "active" : ""
                }`}
                onClick={() => setTimeMode("scheduled")}
              >
                В определённое время
              </button>
            </div>
            {timeMode === "scheduled" && (
              <input
                type="time"
                step="60"
                className="Cart--Input"
                value={orderTime}
                onChange={(e) => setOrderTime(e.target.value)}
              />
            )}
            <input
              type="text"
              placeholder="Номер столика или кабинки"
              className="Cart--Input"
              value={pickupPlace}
              onChange={(e) => setPickupPlace(e.target.value)}
            />
          </div>
        )}

        {deliveryType === "delivery" && (
          <>
            {isLoadingThreshold && (
              <div className="Cart--FreeDeliveryNotice">
                {"Рассчитываем условия доставки..."}
              </div>
            )}
            {!isLoadingThreshold && amountToFreeDelivery > 0 && (
              <div className="Cart--FreeDeliveryNotice">
                {`Нужно ещё ${amountToFreeDelivery.toFixed(
                  0
                )} ₽ для бесплатной доставки`}
              </div>
            )}
            {!isLoadingThreshold && amountToFreeDelivery === 0 && (
              <div className="Cart--FreeDeliveryNotice Cart--FreeDeliveryNotice--success">
                {"Бесплатная доставка!"}
              </div>
            )}
          </>
        )}

        {deliveryType === "delivery" && (
          <div className="Cart--DeliveryForm">
            <input
              type="tel"
              placeholder="Номер телефона"
              className="Cart--Input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div className="Cart--TimeOptions">
              <button
                type="button"
                className={`Time--OptionButton ${
                  timeMode === "asap" ? "active" : ""
                }`}
                onClick={() => setTimeMode("asap")}
              >
                Забрать по готовности
              </button>
              <button
                type="button"
                className={`Time--OptionButton ${
                  timeMode === "scheduled" ? "active" : ""
                }`}
                onClick={() => setTimeMode("scheduled")}
              >
                В определённое время
              </button>
            </div>
            {timeMode === "scheduled" && (
              <input
                type="time"
                step="60"
                className="Cart--Input"
                value={orderTime}
                onChange={(e) => setOrderTime(e.target.value)}
              />
            )}
            <input
              type="text"
              placeholder="Улица, дом, квартира"
              className="Cart--Input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
        )}

        <button
          className="Cart--CheckoutButton"
          onClick={handleCreateOrder}
          disabled={!isFormValid || isSubmitting || isOrderingDisabled}
        >
          {isSubmitting ? "Создаём заказ..." : "Создать заказ"}
        </button>
      </div>
    </div>
  );
};

export default Cart;
